import React, { useState } from 'react';
import './FileSummary.css';
import { Film, Image, FileVideo, Clock, Maximize, Cpu, Layers, Volume2, Music, Hash, LayoutGrid, Copy, Check } from 'lucide-react';

/**
 * FileSummary - displays key file specs in a compact grid below the preview
 * @param {object} compareWith - optional other side's metadata for diff highlighting
 */
function FileSummary({ metadata, file, compareWith }) {
    const [copiedIndex, setCopiedIndex] = useState(null);

    if (!metadata || !file) return null;

    const isVideo = file.type.startsWith('video/');

    const handleCopy = async (value, index) => {
        try {
            await navigator.clipboard.writeText(String(value));
            setCopiedIndex(index);
            setTimeout(() => setCopiedIndex(null), 1200);
        } catch (e) {
            console.warn('Copy failed:', e);
        }
    };

    const specs = [];

    // File name
    specs.push({
        icon: isVideo ? Film : Image,
        label: 'Name',
        value: file.name,
        className: 'spec-name',
    });

    // File type
    specs.push({
        icon: FileVideo,
        label: 'Type',
        value: file.type || 'unknown',
    });

    // File size
    specs.push({
        icon: Hash,
        label: 'Size',
        value: metadata.fileSize || `${(file.size / 1024 / 1024).toFixed(2)} MB`,
    });

    // Resolution
    const width = metadata.width || metadata.ImageWidth;
    const height = metadata.height || metadata.ImageHeight;
    if (width && height) {
        specs.push({
            icon: Maximize,
            label: 'Resolution',
            value: `${width} × ${height}`,
        });
    }

    // Codec
    if (metadata.codec) {
        specs.push({
            icon: Cpu,
            label: 'Codec',
            value: metadata.codec,
        });
    }

    // Frame rate
    if (metadata.frameRate) {
        specs.push({
            icon: Layers,
            label: 'Frame Rate',
            value: metadata.frameRate,
        });
    }

    // Frame count
    const frameCount = metadata.videoMetadata?.Video?.numberOfSamples
        || metadata.AllTracks?.[0]?.numberOfSamples;
    if (frameCount) {
        specs.push({
            icon: LayoutGrid,
            label: 'Frames',
            value: frameCount.toLocaleString(),
        });
    }

    // Duration
    if (metadata.duration) {
        specs.push({
            icon: Clock,
            label: 'Duration',
            value: metadata.duration,
        });
    }

    // Bitrate (from AllTracks or top-level)
    const bitrate = metadata.AllTracks?.[0]?.bitrate || metadata.bitRate;
    if (bitrate) {
        specs.push({
            icon: Hash,
            label: 'Bitrate',
            value: typeof bitrate === 'number' ? `${Math.round(bitrate / 1000)} kbps` : bitrate,
        });
    }

    // Audio info
    const audio = metadata.videoMetadata?.Audio || metadata.Audio;
    if (audio) {
        specs.push({
            icon: Volume2,
            label: 'Audio',
            value: `${audio.codec || 'unknown'}${audio.sampleRate ? ` ${audio.sampleRate}Hz` : ''}${audio.channelCount ? ` ${audio.channelCount}ch` : ''}`,
        });
    } else if (isVideo) {
        specs.push({
            icon: Volume2,
            label: 'Audio',
            value: 'None',
            className: 'spec-muted',
        });
    }

    // Encoder (from UserData or QuickTimeMetadata)
    const encoder = metadata.UserData?.Encoder
        || metadata.QuickTimeMetadata?.software
        || metadata.QuickTimeMetadata?.encoder
        || metadata.Software;
    if (encoder) {
        specs.push({
            icon: Music,
            label: 'Encoder',
            value: encoder,
        });
    }

    // Build comparison lookup if compareWith is provided
    const getCompareValue = (label) => {
        if (!compareWith) return undefined;
        const cw = compareWith;
        switch (label) {
            case 'Name': return cw.fileName;
            case 'Type': return cw.fileType;
            case 'Size': return cw.fileSize;
            case 'Resolution': {
                const w = cw.width || cw.ImageWidth;
                const h = cw.height || cw.ImageHeight;
                return (w && h) ? `${w} × ${h}` : undefined;
            }
            case 'Codec': return cw.codec;
            case 'Frame Rate': return cw.frameRate;
            case 'Frames': {
                const fc = cw.videoMetadata?.Video?.numberOfSamples || cw.AllTracks?.[0]?.numberOfSamples;
                return fc ? fc.toLocaleString() : undefined;
            }
            case 'Duration': return cw.duration;
            case 'Bitrate': {
                const br = cw.AllTracks?.[0]?.bitrate || cw.bitRate;
                return br ? (typeof br === 'number' ? `${Math.round(br / 1000)} kbps` : br) : undefined;
            }
            case 'Audio': {
                const a = cw.videoMetadata?.Audio || cw.Audio;
                return a ? `${a.codec || 'unknown'}${a.sampleRate ? ` ${a.sampleRate}Hz` : ''}${a.channelCount ? ` ${a.channelCount}ch` : ''}` : 'None';
            }
            default: return undefined;
        }
    };

    return (
        <div className="file-summary">
            {specs.map((spec, i) => {
                const otherVal = getCompareValue(spec.label);
                const isDiff = compareWith && otherVal !== undefined && String(otherVal) !== String(spec.value);
                return (
                    <div
                        key={i}
                        className={`spec-row ${spec.className || ''} ${copiedIndex === i ? 'spec-copied' : ''} ${isDiff ? 'spec-diff' : ''}`}
                        onClick={() => handleCopy(spec.value, i)}
                        title={`Click to copy: ${spec.value}`}
                    >
                        <spec.icon size={12} className="spec-icon" />
                        <span className="spec-label">{spec.label}</span>
                        <span className="spec-value">{spec.value}</span>
                        {isDiff && <span className="spec-diff-dot">●</span>}
                        {copiedIndex === i ? (
                            <Check size={10} className="spec-copy-icon spec-check" />
                        ) : (
                            <Copy size={10} className="spec-copy-icon" />
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export default FileSummary;
